import { useState } from 'react';
import { exportToCSV } from '@/utils/export';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Calendar,
  Download,
  Search,
  Shield,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { TransactionQueryParams, walletService } from '@/services/wallet.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface AuditLogTableProps {
  userId?: string; // If provided, shows audit log for specific user only
}

/**
 * Audit Log Table Component
 * Displays all admin wallet adjustments with filtering and export
 */
export function AuditLogTable({ userId }: AuditLogTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<'7' | '30' | '90' | 'all'>('30');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Calculate date range
  const getDateRange = () => {
    if (dateRange === 'all') return undefined;
    const days = parseInt(dateRange);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    return {
      startDate: format(startDate, "yyyy-MM-dd'T'00:00:00"),
      endDate: format(endDate, "yyyy-MM-dd'T'23:59:59"),
    };
  };

  // Fetch audit log (transactions with createdBy)
  const { data, isLoading } = useQuery({
    queryKey: ['audit-log', userId, dateRange, currentPage],
    queryFn: async () => {
      const dateRangeParams = getDateRange();
      const params: TransactionQueryParams = {
        skipCount: (currentPage - 1) * itemsPerPage,
        maxResultCount: itemsPerPage,
        ...dateRangeParams,
      };

      // If userId provided, get transactions for that user and filter by createdBy
      if (userId) {
        const result = await walletService.getUserTransactions(userId, params);
        return {
          items: result.items.filter((t) => t.createdBy),
          totalCount: result.items.filter((t) => t.createdBy).length,
        };
      }

      // Otherwise, use the dedicated audit log endpoint
      return walletService.getAuditLog(params);
    },
  });

  // Filter transactions by search term and createdBy
  const filteredTransactions = (data?.items || []).filter((transaction) => {
    // Only show transactions with createdBy (admin adjustments)
    if (!transaction.createdBy) return false;

    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();
    return (
      transaction.description.toLowerCase().includes(search) ||
      transaction.createdBy?.toLowerCase().includes(search) ||
      transaction.category.toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Export to CSV
  const handleExport = async () => {
    try {
      // Fetch all audit log data
      const dateRangeParams = getDateRange();
      let auditLogs;

      if (userId) {
        // For specific user, get their transactions and filter
        const allData = await walletService.getUserTransactions(userId, {
          maxResultCount: 10000,
          ...dateRangeParams,
        });
        auditLogs = allData.items.filter((t) => t.createdBy);
      } else {
        // Use dedicated audit log endpoint
        const allData = await walletService.getAuditLog({
          maxResultCount: 10000,
          ...dateRangeParams,
        });
        auditLogs = allData.items;
      }

      const csvData = auditLogs.map((transaction) => ({
        Date: format(new Date(transaction.creationTime), 'yyyy-MM-dd HH:mm:ss'),
        Admin: transaction.createdBy || 'System',
        Action: transaction.changeType === 1 ? 'Credit' : 'Debit',
        Amount: transaction.amount.toLocaleString(),
        Description: transaction.description,
        Category: transaction.category,
        'Balance Before': transaction.balanceBefore.toLocaleString(),
        'Balance After': transaction.balanceAfter.toLocaleString(),
      }));

      const columns = [
        { key: 'Date' as const, label: 'Date' },
        { key: 'Admin' as const, label: 'Admin' },
        { key: 'Action' as const, label: 'Action' },
        { key: 'Amount' as const, label: 'Amount' },
        { key: 'Description' as const, label: 'Description' },
        { key: 'Category' as const, label: 'Category' },
        { key: 'Balance Before' as const, label: 'Balance Before' },
        { key: 'Balance After' as const, label: 'Balance After' },
      ];

      exportToCSV(
        csvData,
        columns,
        `audit-log-${format(new Date(), 'yyyy-MM-dd-HHmmss')}`,
      );
      toast.success('Audit log exported successfully');
    } catch (error) {
      toast.error('Failed to export audit log');
      console.error(error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Wallet Audit Log
          </CardTitle>
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by admin, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={dateRange}
            onValueChange={(value) => setDateRange(value as '7' | '30' | '90' | 'all')}
          >
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Shield className="h-4 w-4" />
              Total Adjustments
            </div>
            <div className="text-2xl font-bold">
              {filteredTransactions.length}
            </div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4 text-success" />
              Credits
            </div>
            <div className="text-2xl font-bold">
              {filteredTransactions.filter((t) => t.changeType === 1).length}
            </div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingDown className="h-4 w-4 text-destructive" />
              Debits
            </div>
            <div className="text-2xl font-bold">
              {filteredTransactions.filter((t) => t.changeType === 2).length}
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Shield className="h-12 w-12 animate-pulse text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Loading audit log...</p>
            </div>
          </div>
        ) : currentTransactions.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <div className="text-center">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No audit log entries found</p>
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Admin User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Balance Before</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="font-medium">
                        {format(
                          new Date(transaction.creationTime),
                          'MMM dd, yyyy HH:mm',
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-primary" />
                          <span>{transaction.createdBy || 'System'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {transaction.changeType === 1 ? (
                          <Badge variant="success">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Credit
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <TrendingDown className="h-3 w-3 mr-1" />
                            Debit
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {transaction.changeType === 1 ? '+' : '-'}
                        {transaction.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {transaction.description}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{transaction.category}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {transaction.balanceBefore.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {transaction.balanceAfter.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to{' '}
                  {Math.min(endIndex, filteredTransactions.length)} of{' '}
                  {filteredTransactions.length} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
